import { Component, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectorRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NbCardModule, NbSelectModule, NbButtonModule, NbSpinnerModule, NbIconModule, NbTooltipModule } from '@nebular/theme';
import { Subject, takeUntil } from 'rxjs';
import { CampaignService, SegmentMatrixCell, SegmentMatrixResponse, SegmentMetadataItem } from 'src/app/deebr/service/campaign.service';

export interface MatrixFilterEvent {
    segment_email: string[];   // original labels for CRM filter (e.g. ['Perdu', 'Inactifs', 'Déserteurs'])
    axis_type: string;
    segment_y: string[];       // original labels for Y axis CRM filter
}

interface GridCell {
    rowLabel: string;
    colLabel: string;
    count: number;
    countJ30: number;
    ca30j: number;
    deltaPct: number | null;
    intensity: number; // 0..1 for heatmap
    isTotal?: boolean; // true for total row/col cells
    totalType?: 'row' | 'col' | 'grand'; // which kind of total
}

@Component({
    selector: 'app-segment-matrix',
    standalone: true,
    imports: [CommonModule, FormsModule, NbCardModule, NbSelectModule, NbButtonModule, NbSpinnerModule, NbIconModule, NbTooltipModule],
    templateUrl: './segment-matrix.component.html',
    styleUrls: ['./segment-matrix.component.scss'],
})
export class SegmentMatrixComponent implements OnInit, OnDestroy, OnChanges {
    @Output() addToSegment = new EventEmitter<MatrixFilterEvent>();
    @Input() achatMetadataMap: Record<string, SegmentMetadataItem> = {};
    @Input() emailMetadataMap: Record<string, SegmentMetadataItem> = {};
    @Input() valeurMetadataMap: Record<string, SegmentMetadataItem> = {};
    @Input() channel: string = 'all';

    loading = true;
    error: string | null = null;

    // Raw data from API
    allCells: SegmentMatrixCell[] = [];
    snapshotDate: string | null = null;
    totalClients = 0;
    totalCa30j = 0;
    hasCa = false;

    // Axis selection
    axisTypes: string[] = [];
    selectedAxis = '';
    emailLabels: string[] = [];
    yLabelsByAxis: Record<string, string[]> = {};
    emailLabelMapping: Record<string, string[]> = {}; // grouped → originals
    yLabelMapping: Record<string, Record<string, string[]>> = {}; // axis → grouped → originals

    // Computed grid
    colLabels: string[] = [];
    rowLabels: string[] = [];
    grid: GridCell[][] = [];

    // Selected cell detail
    selectedCell: GridCell | null = null;

    // Stats
    actifsEmail = 0;

    private destroy$ = new Subject<void>();

    constructor(private campaignService: CampaignService, private cd: ChangeDetectorRef) {}

    ngOnInit() {
        this.loadData();
    }

    ngOnChanges(changes: SimpleChanges) {
        // Recharger la matrice quand le channel change (apres init)
        if (changes['channel'] && !changes['channel'].firstChange) {
            this.loadData();
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadData() {
        this.loading = true;
        this.error = null;
        this.campaignService.getSegmentMatrix(this.channel)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: SegmentMatrixResponse) => {
                    this.allCells = res.cells;
                    this.snapshotDate = res.snapshot_date;
                    this.totalClients = res.total_clients;
                    this.totalCa30j = res.total_ca_30j ?? 0;
                    this.hasCa = res.total_ca_30j != null;
                    this.axisTypes = res.axis_types;
                    this.emailLabels = res.email_labels;
                    this.yLabelsByAxis = res.y_labels_by_axis;
                    this.emailLabelMapping = res.email_label_mapping ?? {};
                    this.yLabelMapping = res.y_label_mapping ?? {};

                    // Default to first axis
                    this.selectedAxis = this.axisTypes[0] ?? '';
                    this.rebuildView();
                    this.loading = false;
                    this.cd.detectChanges();
                },
                error: (err) => {
                    this.error = 'Impossible de charger la matrice de segmentation';
                    this.loading = false;
                    this.cd.detectChanges();
                    console.error('segment-matrix load error', err);
                }
            });
    }

    onAxisChange() {
        this.selectedCell = null;
        this.rebuildView();
    }

    private rebuildView() {
        // Filter cells for current axis
        const cells = this.allCells.filter(c => c.axis_type === this.selectedAxis);

        // Labels: email on X (columns), segment_y on Y (rows)
        this.colLabels = [...this.emailLabels, 'Total'];
        this.rowLabels = [...(this.yLabelsByAxis[this.selectedAxis] ?? []), 'Total'];

        // Compute stats (on current axis only, to avoid double-counting)
        this.computeStats(cells);

        // Build lookup
        const map = new Map<string, SegmentMatrixCell>();
        for (const c of cells) {
            map.set(`${c.segment_y}||${c.segment_email}`, c);
        }

        // Find max count for heatmap (exclude totals)
        let maxCount = 0;
        for (const c of cells) {
            if (c.count > maxCount) maxCount = c.count;
        }

        const dataRowLabels = this.yLabelsByAxis[this.selectedAxis] ?? [];
        const dataColLabels = this.emailLabels;

        // Build grid (data rows + total row)
        const tempGrid: GridCell[][] = [];
        for (const rowLabel of this.rowLabels) {
            const row: GridCell[] = [];
            const isRowTotal = rowLabel === 'Total';

            for (const colLabel of this.colLabels) {
                const isColTotal = colLabel === 'Total';

                if (!isRowTotal && !isColTotal) {
                    // Normal cell — recalculate delta from absolute counts
                    const c = map.get(`${rowLabel}||${colLabel}`);
                    const count = c?.count ?? 0;
                    const countJ30 = c?.count_j30 ?? 0;
                    const deltaPct = countJ30 > 0 ? (count - countJ30) / countJ30 : null;
                    row.push({
                        rowLabel, colLabel, count, countJ30,
                        ca30j: c?.ca_30j ?? 0,
                        deltaPct,
                        intensity: maxCount > 0 ? count / maxCount : 0,
                    });
                } else if (isRowTotal && !isColTotal) {
                    // Column total: sum all rows for this column
                    const sum = this.sumCells(dataRowLabels, colLabel, map);
                    row.push({ ...sum, rowLabel: 'Total', colLabel, isTotal: true, totalType: 'col' });
                } else if (!isRowTotal && isColTotal) {
                    // Row total: sum all columns for this row
                    const sum = this.sumCells([rowLabel], dataColLabels, map, true);
                    row.push({ ...sum, rowLabel, colLabel: 'Total', isTotal: true, totalType: 'row' });
                } else {
                    // Grand total
                    const sum = this.sumCells(dataRowLabels, dataColLabels, map, true);
                    row.push({ ...sum, rowLabel: 'Total', colLabel: 'Total', isTotal: true, totalType: 'grand' });
                }
            }
            tempGrid.push(row);
        }
        this.grid = tempGrid;
    }

    /** Sum cells across given rows/cols. If cols is an array, iterate all combos. */
    private sumCells(
        rows: string[] | string, cols: string[] | string,
        map: Map<string, SegmentMatrixCell>, multiCol = false
    ): GridCell {
        const rowArr = Array.isArray(rows) ? rows : [rows];
        const colArr = Array.isArray(cols) ? cols : [cols];
        let count = 0, countJ30 = 0, ca30j = 0;
        for (const r of rowArr) {
            for (const c of colArr) {
                const cell = map.get(`${r}||${c}`);
                if (cell) {
                    count += cell.count;
                    countJ30 += cell.count_j30;
                    ca30j += cell.ca_30j ?? 0;
                }
            }
        }
        const deltaPct = countJ30 > 0 ? (count - countJ30) / countJ30 : null;
        return {
            rowLabel: '', colLabel: '', count, countJ30, ca30j, deltaPct, intensity: 0,
        };
    }

    private computeStats(cells: SegmentMatrixCell[]) {
        // Actifs email : count for cells where segment_email contains engagement keywords
        // Since labels are dynamic, sum cells where count > 0 for non-"Inactifs"/"Perdu"/"Déserteurs" email segments
        const inactiveKeywords = ['inactif', 'perdu', 'déserteur', 'dormant'];
        this.actifsEmail = cells
            .filter(c => !inactiveKeywords.some(k => c.segment_email.toLowerCase().includes(k)))
            .reduce((sum, c) => sum + c.count, 0);
    }

    selectCell(cell: GridCell) {
        this.selectedCell = this.selectedCell === cell ? null : cell;
    }

    onAddToSegment() {
        if (!this.selectedCell) return;
        if (this.selectedCell.totalType === 'grand') return;

        const colLabel = this.selectedCell.colLabel;
        // Resolve grouped label → original labels via mapping
        let emailOriginals: string[];
        if (colLabel === 'Total') {
            emailOriginals = [];
        } else {
            emailOriginals = this.emailLabelMapping[colLabel] ?? [colLabel];
        }

        const rowLabel = this.selectedCell.rowLabel;
        let yOriginals: string[];
        if (rowLabel === 'Total') {
            yOriginals = [];
        } else {
            yOriginals = this.yLabelMapping[this.selectedAxis]?.[rowLabel] ?? [rowLabel];
        }

        const event: MatrixFilterEvent = {
            segment_email: emailOriginals,
            axis_type: this.selectedAxis,
            segment_y: yOriginals,
        };
        this.addToSegment.emit(event);
    }

    getHeaderTooltip(label: string, axis: 'email' | 'y'): string {
        if (label === 'Total') return '';
        const key = (label ?? '').normalize('NFC').trim();

        // Pour l'axe email, le label peut etre un GROUPE (ex: "Engagés" = Engagés + Fidèles).
        // Fusion des ranges des sous-segments en une seule plage.
        if (axis === 'email') {
            const originals = this.emailLabelMapping[key];
            if (originals && originals.length > 1) {
                const merged = this.mergeEmailCriteria(originals);
                return merged || '';
            }
            const meta = this.emailMetadataMap[key];
            if (!meta) return '';
            const parts: string[] = [];
            if (meta.description) parts.push(meta.description);
            if (meta.criteria) parts.push(`Critère: ${meta.criteria}`);
            return parts.join(' — ');
        }

        // Axe Y (achats ou valeur)
        const meta = this.selectedAxis === 'valeur'
            ? this.valeurMetadataMap[key]
            : this.achatMetadataMap[key];
        if (!meta) return '';
        const parts: string[] = [];
        if (meta.description) parts.push(meta.description);
        if (meta.criteria) parts.push(`Critère: ${meta.criteria}`);
        return parts.join(' — ');
    }

    // Formatting helpers
    formatCount(n: number): string {
        if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
        if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
        return n.toString();
    }

    formatCa(n: number): string {
        if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M€';
        if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K€';
        return Math.round(n) + '€';
    }

    formatDelta(d: number | null): string {
        if (d == null) return '';
        const pct = Math.round(d * 1000) / 10; // d is already a ratio like 1.1438
        return (pct >= 0 ? '+' : '') + pct.toFixed(1) + '%';
    }

    getCellBg(cell: GridCell): string {
        const lightness = 95 - cell.intensity * 60;
        return `hsl(210, 70%, ${lightness}%)`;
    }

    getCellTextColor(cell: GridCell): string {
        return cell.intensity > 0.5 ? '#ffffff' : '#1a1a2e';
    }

    /** Parse criteria text like "entre 0.40 et 0.60", ">= 0.95", "= 0" → numeric range. */
    private parseScoreRange(criteria: string): { lower: number; upper: number; unbounded: boolean } | null {
        if (!criteria) return null;
        let m = criteria.match(/entre\s+([\d.]+)\s+et\s+([\d.]+)/i);
        if (m) return { lower: parseFloat(m[1]), upper: parseFloat(m[2]), unbounded: false };
        m = criteria.match(/>=\s*([\d.]+)/);
        if (m) return { lower: parseFloat(m[1]), upper: 1.0, unbounded: true };
        m = criteria.match(/=\s*([\d.]+)/);
        if (m) {
            const v = parseFloat(m[1]);
            return { lower: v, upper: v, unbounded: false };
        }
        return null;
    }

    /** Combine criteria + stats of several sub-segments into a single tooltip. */
    private mergeEmailCriteria(originals: string[]): string {
        const items = originals
            .map(o => this.emailMetadataMap[o])
            .filter(m => m);

        // Range fusionné des scores
        const ranges = items
            .map(m => this.parseScoreRange(m.criteria || ''))
            .filter((r): r is { lower: number; upper: number; unbounded: boolean } => r !== null);

        let rangeStr = '';
        if (ranges.length > 0) {
            const lower = Math.min(...ranges.map(r => r.lower));
            const upper = Math.max(...ranges.map(r => r.upper));
            const hasUnbounded = ranges.some(r => r.unbounded);
            rangeStr = hasUnbounded
                ? `Score engagement >= ${lower}`
                : `Score engagement entre ${lower} et ${upper}`;
        }

        // Moyennes ponderees sur le count
        const totalCount = items.reduce((s, m) => s + (m.count || 0), 0);
        const weighted = (field: keyof typeof items[0]): number | null => {
            let num = 0, den = 0;
            for (const m of items) {
                const v = m[field] as number | null | undefined;
                if (v == null) continue;
                const w = m.count || 0;
                num += v * w;
                den += w;
            }
            return den > 0 ? num / den : null;
        };
        const avgReact = weighted('avg_reactivity');
        const avgDays = weighted('avg_days_since_last_open');

        const parts: string[] = [];
        if (avgReact != null) parts.push(`ouvrent en moyenne ${Math.round(avgReact * 100)}% des emails reçus`);
        if (avgDays != null) parts.push(`dernière ouverture il y a ${Math.round(avgDays)} jours`);

        const descStr = parts.length ? parts.join('. ') + '.' : '';

        if (rangeStr && descStr) return `${descStr} — Critère: ${rangeStr}`;
        return rangeStr || descStr;
    }

    getAxisLabel(axis: string): string {
        const labels: Record<string, string> = {
            'achats': 'Achats',
            'fidelite': 'Fidélité',
            'valeur': 'Propension',
        };
        return labels[axis] ?? axis.charAt(0).toUpperCase() + axis.slice(1);
    }
}
